import React, { useEffect, useState, useCallback, useRef } from "react";
import { useThree } from "@react-three/fiber";
import * as THREE from "three";
import { useLevelProgress } from "../../stores/useLevelProgress";
import { Lighting } from "../objects";
import { useUI } from "../../stores/useUI";
import type { MapData } from "../../types/MapData.types";
import MapGround from "./MapGround";
import MapBoundaries from "./MapBoundaries";
import MapObjects from "./MapObjects";
import MapNPCs from "./MapNPCs";

interface MapRendererProps {
  mapId: string;
  onComplete?: () => void;
}

export default function MapRenderer({ mapId, onComplete }: MapRendererProps) {
  const { scene } = useThree();
  const [mapData, setMapData] = useState<MapData | null>(null);
  const [categoryRoutes, setCategoryRoutes] = useState<number[][][]>([]);
  const [mapNameById, setMapNameById] = useState<Record<string, string>>({});
  const loading = useRef<boolean>(true);
  const progress = useLevelProgress((state) => state.progress);
  const completeNPC = useLevelProgress((state) => state.completeNPC);
  const registerMapDataItems = useUI((state) => state.registerMapDataItems);

  useEffect(() => {
    const loadMapData = async () => {
      try {
        loading.current = true;
        const [response, worldConfigResponse] = await Promise.all([
          fetch(`/data/maps/map${mapId}.json`),
          fetch(`/data/worldConfig.json`),
        ]);
        const data = await response.json() as MapData;
        const worldConfig = await worldConfigResponse.json();
        const nameById: Record<string, string> = {};
        for (const map of worldConfig.maps) {
          nameById[map.id] = map.name;
        }
        setMapNameById(nameById);
        const layoutName = data.category;

        const [layout, envResponse] = await Promise.all([
          fetch(`/data/layouts/layout_${layoutName}.json`).then(r => r.json()),
          fetch(`/data/envorinments/env-${layoutName}.json`).then(r => r.json()),
        ]);

        data.terrain = { ...envResponse.terrain, objects: layout.objects };
        data.environment = envResponse.environment;

        try {
          const routesResponse = await fetch(`/data/npc_routes/routes_${layoutName}.json`);
          if (routesResponse.ok) {
            const routesData = await routesResponse.json();
            setCategoryRoutes(routesData.routes || []);
          }
        } catch {
          // немає файлу роутів — використовуємо роути з map.json
        }

        registerMapDataItems(data);
        setMapData(data);
      } catch (error) {
        console.error(`Failed to load map ${mapId}:`, error);
      } finally {
        loading.current = false;
      }
    };

    loadMapData();
  }, [mapId]);

  useEffect(() => {
    if (!mapData) return;
    if (mapData.environment.fog.enabled) {
      scene.fog = new THREE.Fog(
        mapData.environment.fog.color,
        mapData.environment.fog.near,
        mapData.environment.fog.far
      );
    }
    return () => { scene.fog = null; };
  }, [scene, mapData]);

  const handleNPCComplete = useCallback((npcId: string) => {
    completeNPC(mapId, npcId);
    if (onComplete) onComplete();
  }, [mapId, completeNPC, onComplete]);

  if (loading.current || !mapData) return null;

  return (
    <>
      <Lighting
        ambient={mapData.environment.lighting.ambient}
        directional={mapData.environment.lighting.directional}
      />
      <MapGround
        terrain={mapData.terrain}
        environment={mapData.environment}
        mapId={mapId}
      />
      <MapBoundaries ground={mapData.terrain.ground} />
      <MapObjects
        terrain={mapData.terrain}
        category={mapData.category}
        mapId={mapId}
      />
      <MapNPCs
        npcs={mapData.npcs}
        mapId={mapId}
        progress={progress as any}
        categoryRoutes={categoryRoutes}
        onNPCComplete={handleNPCComplete}
        mapNameById={mapNameById}
      />
    </>
  );
}
